/**
 * Field helpers for `LeadFormEmbed` — plain functions kept out of the component
 * file so it exports only components (react-refresh), and unit-tested directly
 * (LeadFormEmbed.test.jsx).
 */

/**
 * Core input fields written to `formData` (not `extra`). The `channel` field is
 * handled separately (the preferred-contact chooser); anything else is custom →
 * collected into the lead `extra`.
 */
export const CORE_INPUT_KEYS = ['name', 'phone', 'email', 'message'];

/** True for a `channel`-type field entry. */
export function isChannelField(f) {
  return f.type === 'channel' || f.key === 'channel';
}

// ─── #1469 — walk-through, slider, "Don't know yet" ─────────────────────────

/** Label + submitted value of the "Don't know yet" choice when unset. */
export const DEFAULT_UNKNOWN_LABEL = "Don't know yet";

/** Viewport where `appearance.walkthrough: 'mobile'` (the default) kicks in. */
export const WALKTHROUGH_MEDIA = '(max-width: 767px)';

/**
 * Slider bounds of a `range` field with defaults and sanity applied — a template
 * typo (max ≤ min, zero increment, default out of range) must not break the form.
 *
 * @returns {{ min: number, max: number, increment: number, initial: number }}
 */
export function rangeConfig(field) {
  const min = Number.isFinite(field.min) ? field.min : 0;
  const max = Number.isFinite(field.max) && field.max > min ? field.max : min + 100;
  const increment =
    Number.isFinite(field.increment) && field.increment > 0 ? field.increment : 1;
  const raw = Number.isFinite(field.defaultValue) ? field.defaultValue : min;
  return { min, max, increment, initial: Math.min(max, Math.max(min, raw)) };
}

/** Slider value as shown and sent: "1 month" / "12 months" / "40" (no unit). */
export function formatRangeValue(field, value) {
  if (!field.unit) return String(value);
  const unit = Number(value) === 1 ? field.unit : field.unitPlural || `${field.unit}s`;
  return `${value} ${unit}`;
}

/** Text of the "Don't know yet" choice — also the value sent when it's ticked. */
export function unknownLabelOf(field) {
  return field.unknownLabel || DEFAULT_UNKNOWN_LABEL;
}

/**
 * "Don't know yet" is offered on custom single-control fields only: core inputs
 * (name/phone/email/message) and the channel / radio groups don't get it.
 */
export function canBeUnknown(field) {
  return (
    field.allowUnknown === true &&
    !CORE_INPUT_KEYS.includes(field.key) &&
    !isChannelField(field) &&
    field.type !== 'radio'
  );
}

/** Initial value of a custom field: a slider starts on its default, the rest empty. */
export function initialCustomValue(field) {
  return field.type === 'range' ? String(rangeConfig(field).initial) : '';
}

/**
 * Walk-through screens: the sorted distinct `step` numbers of the enabled fields
 * (no `step` → 1). A single entry means the form has no walk-through.
 *
 * @returns {number[]}
 */
export function walkthroughSteps(fieldList) {
  const steps = new Set();
  for (const f of fieldList) {
    if (f.enabled === false) continue;
    steps.add(f.step || 1);
  }
  return [...steps].sort((a, b) => a - b);
}

/**
 * Is the walk-through on? Needs 2+ screens; then `appearance.walkthrough` decides:
 * 'off' → never, 'always' → everywhere, 'mobile' / unset → narrow viewports only.
 */
export function isWalkthroughActive({ steps, mode, isNarrow }) {
  if (!Array.isArray(steps) || steps.length < 2) return false;
  if (mode === 'off') return false;
  if (mode === 'always') return true;
  return !!isNarrow;
}
